import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Typography
} from '@mui/material'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'

import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { Formik } from 'formik'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import {
  type AssetHolderEmailJobPayload,
  validateAssetHolderEmailJobPayload
} from '../../../../api-schema-types/asset-holdings/AssetHolderPayloadDto'
import {
  useGetMailGroupKeysQuery,
  useGetMailStatusQuery
} from '../../../../api-services/asset-holdings/assetHolderApi'
import {
  type EmailNotificationsGroupKeyDetail,
  type EmailNotificationsStatusCounts
} from '../../../../api-schema-types/asset-holdings/AssetHolderDomainDtos'
import EmailSendingStatusChip from '../../../components/chips/EmailSendingStatusChip'
import Refresh from '@mui/icons-material/Refresh'
import HomeIcon from '@mui/icons-material/Home'

const EmailNotificationsPage: React.FC = (props: any) => {
  const history = useHistory()

  const [reportType, setReportType] = useState<string>('SOLAR_PAYMENTS_REPORT')
  const [groupKey, setGroupKey] = useState<string>('')
  const skipQuery = groupKey === ''
  const goTo = (location: string): void => {
    history.push(location)
  }

  const emailNotificationsPayload: AssetHolderEmailJobPayload = {
    reportType,
    groupKey
  }

  const getMailGroupKeys = useGetMailGroupKeysQuery({ reportType })
  const getMailStatus = useGetMailStatusQuery({ reportType, groupKey }, { skip: skipQuery })
  const refetchGetMailGroupKeys = getMailGroupKeys.refetch
  const refetchGetMailStatus = getMailStatus.refetch

  const handleReportTypeChange = (event: SelectChangeEvent): void => {
    setReportType(event.target.value)
  }
  const handleGroupKeyChange = (event: SelectChangeEvent): void => {
    setGroupKey(event.target.value)
  }

  const handleCreateEmailAction = (): void => {
    goTo('/app/distributions/emails/create')
  }

  const handleBackAction = (): void => {
    goTo('/app/finance-area')
  }

  const handleRefresh = async (): Promise<void> => {
    if (groupKey !== '') {
      await refetchGetMailStatus()
    } else {
      await refetchGetMailGroupKeys()
    }
  }

  const renderEmailStatus = (statuses: EmailNotificationsStatusCounts[]): React.ReactElement[] => {
    const statusCounters: React.ReactElement[] = []
    for (const detail of statuses) {
      statusCounters.push(<EmailSendingStatusChip key={detail.status} itemStatus={detail.status}
                                                  statusCount={detail.countPerStatus}/>)
    }
    return statusCounters
  }

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ p: 1, flexGrow: 1 }}>
          <Typography variant={'h4'}>Weekly Distributions</Typography>
        </Box>
        <Box sx={{ p: 1 }}>
          <Button variant={'outlined'} type="button" onClick={handleRefresh}>
            <Refresh/>
          </Button>
        </Box>
        <Box sx={{ p: 1 }}>
          <Button variant={'outlined'} type="button" onClick={handleBackAction}>
            <HomeIcon/>
          </Button>
        </Box>
      </Box>
      <Formik
        initialValues={emailNotificationsPayload}
        validationSchema={validateAssetHolderEmailJobPayload}
        onSubmit={async (values, actions) => {
          // await giftFreeCellsOnSubmit(values)
        }}
      >
        {formikProps => (

          <form
            autoComplete={'off'}
            autoCorrect={'off'}
            onSubmit={formikProps.handleSubmit}
          >

            {!formikProps.isValid && (
              <Grid item md={12} sx={{ mt: 3 }}>
                <ErrorComponent title={'Form validation error'} message={JSON.stringify(formikProps.errors)}/>
              </Grid>
            )}

            {getMailGroupKeys.isLoading && <LoadingComponent/>}
            {(getMailGroupKeys.error != null) &&
              <ErrorComponent message={RtkQueryHelper.mapResponseError(getMailGroupKeys.error)}/>}
            {getMailGroupKeys.isSuccess && (
              <Paper>
                <Box sx={{ p: 4, mt: 3 }}>
                  <Grid container sx={{ mb: 4 }}>
                    <Grid item md={3}>
                      <FormControl sx={{ width: '100%' }}>
                        <InputLabel>Report Type</InputLabel>
                        <Select
                          value={reportType}
                          label={'Report Type'}
                          onChange={handleReportTypeChange}
                        >
                          <MenuItem key={'SOLAR_GENERATION_REPORT'} value={'SOLAR_GENERATION_REPORT'}>Solar Generation
                            Report</MenuItem>
                          <MenuItem key={'SOLAR_PAYMENTS_REPORT'} value={'SOLAR_PAYMENTS_REPORT'}>Solar Payments
                            Report</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid container sx={{ mb: 4 }}>
                    <Grid item md={6}>
                      <FormControl sx={{ width: '100%' }}>
                        <InputLabel>Group Key</InputLabel>
                        <Select
                          value={groupKey}
                          label={'Group Key'}
                          onChange={handleGroupKeyChange}
                        >
                          {
                            getMailGroupKeys.isSuccess && getMailGroupKeys.data.groupKeys.map((c: EmailNotificationsGroupKeyDetail) =>
                              <MenuItem key={c.distinctGroupKeys} value={c.distinctGroupKeys}>[{c.distinctGroupKeys}]
                                ({c.finalSentAt})</MenuItem>
                            )
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid container sx={{ mb: 4 }}>
                    <Grid item md={6}>
                      {getMailStatus.isLoading && <LoadingComponent/>}
                      {(getMailStatus.error != null) &&
                        <ErrorComponent message={RtkQueryHelper.mapResponseError(getMailStatus.error)}/>}
                      {getMailStatus.isSuccess && (
                        renderEmailStatus(getMailStatus.data.statusCounts)
                      )}
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 4 }}/>

                  <Grid container spacing={4}>
                    <Grid item lg={9} sx={{ mt: 3 }}>
                      <Button variant={'contained'} startIcon={<CardGiftcardIcon/>} onClick={() => {
                        handleCreateEmailAction()
                      }} sx={{ mr: 3 }}>
                        Create New Email Group
                      </Button>
                      <Button type={'button'} variant={'text'} onClick={() => {
                        handleBackAction()
                      }}>
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            )}
          </form>

        )}
      </Formik>
    </>
  )
}

export default EmailNotificationsPage
